import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import apiService from "../../../../../service/Api";

interface CrewMember {
  rank: number;
  crew_member_id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  streams_attended: number;
  profile_picture?: string | null;
}

interface CrewAttendanceLeaderboardResponse {
  month: string;
  total_participants: number;
  leaderboard: CrewMember[];
}

const getGradientByName = (name: string) => {
  const hash = name.split("").reduce((acc, char) => char.charCodeAt(0) + ((acc << 5) - acc), 0);
  const colors = [
    ["#ff9a9e", "#fecfef"],
    ["#a1c4fd", "#c2e9fb"],
    ["#76b852", "#8DC26F"],
    ["#f12711", "#f5af19"],
    ["#a80077", "#667db6"],
    ["#3a7bd5", "#3a6073"],
    ["#f857a6", "#ff5858"],
    ["#ffd1ff", "#f107a3"],
    ["#11998e", "#38ef7d"],
  ];
  const idx = Math.abs(hash) % colors.length;
  return `linear-gradient(135deg, ${colors[idx][0]} 0%, ${colors[idx][1]} 100%)`;
};

const StreamsLeaderboard = () => {
  const [members, setMembers] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        const now = new Date();
        const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
        const res = await apiService.get<CrewAttendanceLeaderboardResponse>(
          `/api/management/dashboard/crew-attendance-leaderboard/?month=${month}`,
        );
        setMembers(res.data?.leaderboard ?? []);
      } catch (err: unknown) {
        const errorMessage =
          (err as { message?: string })?.message ||
          "Failed to fetch attendance leaderboard.";
        setError(errorMessage);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 250,
          bgcolor: "#0f172a",
          borderRadius: 3,
          mt: 2,
        }}
      >
        <CircularProgress size={36} sx={{ color: "#38ef7d" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2, bgcolor: "#1e293b", color: "#f87171" }}>
        {error}
      </Alert>
    );
  }

  return <LeaderboardCard members={members} />;
};

const LeaderboardCard = ({ members }: { members: CrewMember[] }) => {
  const topThree = members.slice(0, 3);
  const remaining = members.slice(3);

  // Map topThree by rank to ease positioning
  const rank1 = topThree.find((m) => m.rank === 1) || topThree[0];
  const rank2 = topThree.find((m) => m.rank === 2) || topThree[1];
  const rank3 = topThree.find((m) => m.rank === 3) || topThree[2];

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        background: "linear-gradient(180deg, #0c152b 0%, #070d19 100%)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.4)",
        mt: 2,
        p: 2.5,
        color: "#ffffff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            letterSpacing: "0.05em",
            color: "#ffffff",
            textTransform: "uppercase",
            fontSize: "14px",
          }}
        >
          Leaderboard
        </Typography>
        <Box
          sx={{
            bgcolor: "rgba(56, 239, 125, 0.15)",
            color: "#38ef7d",
            px: 1.5,
            py: 0.5,
            borderRadius: 3,
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
          }}
        >
          Active Crew
        </Box>
      </Box>

      {/* Podium (Top 3) */}
      {members.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            gap: 2,
            mb: 4,
            pt: 4,
            pb: 2,
            borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
          }}
        >
          {/* Rank 2 */}
          {rank2 && (
            <PodiumColumn
              member={rank2}
              rank={2}
              avatarSize={60}
              diamondSize={50}
              gradient="linear-gradient(135deg, #a8c0ff 0%, #3f2b96 100%)"
              height={100}
            />
          )}

          {/* Rank 1 */}
          {rank1 && (
            <PodiumColumn
              member={rank1}
              rank={1}
              avatarSize={75}
              diamondSize={60}
              gradient="linear-gradient(135deg, #4ecdc4 0%, #2980b9 100%)"
              height={130}
              hasCrown
            />
          )}

          {/* Rank 3 */}
          {rank3 && (
            <PodiumColumn
              member={rank3}
              rank={3}
              avatarSize={60}
              diamondSize={50}
              gradient="linear-gradient(135deg, #e100ff 0%, #7f00ff 100%)"
              height={90}
            />
          )}
        </Box>
      )}

      {/* Ranks 4+ List */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, flexGrow: 1 }}>
        {members.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "rgba(255, 255, 255, 0.4)", textAlign: "center", py: 4 }}
          >
            No leaderboard data available.
          </Typography>
        ) : remaining.length === 0 && members.length <= 3 ? (
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.3)", textAlign: "center", display: "block" }}
          >
            Top performers shown above
          </Typography>
        ) : (
          remaining.map((member) => (
            <LeaderboardListRow key={member.crew_member_id} member={member} />
          ))
        )}
      </Box>
    </Paper>
  );
};

const PodiumColumn = ({
  member,
  rank,
  avatarSize,
  diamondSize,
  gradient,
  height,
  hasCrown = false,
}: {
  member: CrewMember;
  rank: number;
  avatarSize: number;
  diamondSize: number;
  gradient: string;
  height: number;
  hasCrown?: boolean;
}) => {
  const fullName = `${member.first_name} ${member.last_name}`;
  const initials = `${member.first_name.charAt(0)}${member.last_name.charAt(0)}`.toUpperCase();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: 100,
        position: "relative",
      }}
    >
      {/* Crown */}
      {hasCrown && (
        <Box
          sx={{
            position: "absolute",
            top: -24,
            zIndex: 10,
            animation: "bounce 2s infinite ease-in-out",
            "@keyframes bounce": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-4px)" },
            },
          }}
        >
          <span style={{ fontSize: "28px", filter: "drop-shadow(0 4px 6px rgba(255,215,0,0.4))" }}>👑</span>
        </Box>
      )}

      {/* Avatar Container */}
      <Box sx={{ position: "relative", mb: 1, zIndex: 2 }}>
        <Avatar
          src={member.profile_picture || undefined}
          sx={{
            width: avatarSize,
            height: avatarSize,
            background: getGradientByName(fullName),
            border: rank === 1 ? "3px solid #ffd700" : "2px solid rgba(255, 255, 255, 0.3)",
            boxShadow:
              rank === 1
                ? "0 0 16px rgba(255, 215, 0, 0.5)"
                : "0 4px 12px rgba(0, 0, 0, 0.3)",
            fontSize: rank === 1 ? "22px" : "18px",
            fontWeight: 800,
            color: "#ffffff",
          }}
        >
          {initials}
        </Avatar>
      </Box>

      {/* Diamond and Pillar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          width: "100%",
          height: height,
        }}
      >
        {/* Diamond shape */}
        <Box
          sx={{
            width: diamondSize,
            height: diamondSize,
            transform: "rotate(45deg)",
            borderRadius: "10px",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.3)",
            border: "1.5px solid rgba(255, 255, 255, 0.15)",
            position: "absolute",
            top: -diamondSize / 2,
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              transform: "rotate(-45deg)",
              fontWeight: 900,
              fontSize: rank === 1 ? "20px" : "16px",
              color: "#ffffff",
            }}
          >
            {rank}
          </Typography>
        </Box>
      </Box>

      {/* Name and Score */}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: "#ffffff",
          mt: 1.5,
          textAlign: "center",
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        {member.first_name}
      </Typography>
      <Box
        sx={{
          bgcolor: "rgba(255, 255, 255, 0.08)",
          px: 1.2,
          py: 0.2,
          borderRadius: 2,
          mt: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            color: rank === 1 ? "#ffd700" : rank === 2 ? "#a8c0ff" : "#e100ff",
            fontSize: "11px",
          }}
        >
          {member.streams_attended} str
        </Typography>
      </Box>
    </Box>
  );
};

const LeaderboardListRow = ({ member }: { member: CrewMember }) => {
  const fullName = `${member.first_name} ${member.last_name}`;
  const initials = `${member.first_name.charAt(0)}${member.last_name.charAt(0)}`.toUpperCase();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: 3,
        bgcolor: "rgba(255, 255, 255, 0.04)",
        border: "1px solid rgba(255, 255, 255, 0.03)",
        px: 2,
        py: 1.5,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: "translateX(4px)",
          bgcolor: "rgba(255, 255, 255, 0.07)",
          borderColor: "rgba(255, 255, 255, 0.08)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Avatar with absolute rank badge */}
        <Box sx={{ position: "relative" }}>
          <Avatar
            src={member.profile_picture || undefined}
            sx={{
              width: 38,
              height: 38,
              background: getGradientByName(fullName),
              fontWeight: 700,
              fontSize: "14px",
              color: "#ffffff",
            }}
          >
            {initials}
          </Avatar>
          <Box
            sx={{
              position: "absolute",
              top: -4,
              left: -4,
              width: 18,
              height: 18,
              borderRadius: "50%",
              bgcolor: "#1e293b",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "9px",
                fontWeight: 800,
                color: "#cbd5e1",
              }}
            >
              {member.rank}
            </Typography>
          </Box>
        </Box>

        {/* Crew Name */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: "#ffffff",
            ml: 2,
            textTransform: "capitalize",
          }}
        >
          {member.first_name} {member.last_name.toLowerCase()}
        </Typography>
      </Box>

      {/* Total Streams Badge */}
      <Box
        sx={{
          bgcolor: "rgba(56, 239, 125, 0.12)",
          color: "#38ef7d",
          px: 1.8,
          py: 0.4,
          borderRadius: 4,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 800, fontSize: "12px" }}>
          {member.streams_attended} streams
        </Typography>
      </Box>
    </Box>
  );
};

export default StreamsLeaderboard;
